// VRG-640 — vera.inc website analytics (internal). API client for
// `GET /internal/website-analytics/*`.
//
// Types are declared locally, matching `tourAnalyticsApi.ts`: apps/platform does not
// depend on @repo/types for internal endpoints. Canonical definitions:
// `apps/api/src/features/website-analytics/website-analytics.types.ts`. Keep in step.
//
// Nothing here ever holds a Vercel or PostHog credential — the API owns both and the
// browser only ever sees figures (`website-analytics.guard.spec.ts` scans this app's
// source for the env names to keep it that way).

import { createApiCall } from './index'

export type SourceStatus = 'ok' | 'fixture' | 'not_configured' | 'error'

export interface SourceState {
  status: SourceStatus
  message?: string
}

export interface DayRange {
  since: string
  until: string
  /** "Today": the instant of the viewer's local midnight (ISO). The API cuts there. */
  from?: string
}

export type WebsiteFunnelEvent =
  | 'request_access_opened'
  | 'request_access_submitted'
  | 'roi_started'
  | 'roi_calculated'
  | 'contact_submitted'
  | 'subscribed'

export type WebsiteEventCounts = Record<WebsiteFunnelEvent, number>

export interface WebsiteAnalyticsOverview {
  range: DayRange
  computedAt: string
  driver: 'live' | 'log'
  sources: { vercel: SourceState; posthog: SourceState; vera: SourceState }
  allVisitors: {
    origin: 'live' | 'snapshot' | 'none'
    visitors: number | null
    pageviews: number | null
    events: WebsiteEventCounts | null
  }
  consented: {
    visitors: number
    /** Distinct sessions with a pageview (PostHog). */
    sessions: number
    events: WebsiteEventCounts
    becameClient: number
  } | null
  vera: {
    /** Every signups row created in the range (waitlist, ROI-only senders, staff-added). */
    signups: number
    /** VRG-689 — the waitlist requests among them ("Joined waitlist"). */
    waitlist: number
    /** VRG-689 — ROI models received in the range (one per email). */
    roiSubmitted: number
    grants: number
    demos: number
    companies: number
    becameClient: number
  } | null
  breakdown: {
    referrers: WebsiteSourceRow[]
    paths: WebsiteSourceRow[]
    devices: WebsiteSourceRow[]
    campaigns: WebsiteCampaignRow[]
  }
  map: {
    countries: Array<{ country: string; visitors: number; pageviews: number }>
    cities: Array<{
      country: string
      city: string
      lat: number | null
      lng: number | null
      visitors: number
    }>
  }
}

export interface WebsiteSourceRow {
  value: string
  visitors: number | null
  pageviews: number | null
  formSubmitted: number | null
  signups: number | null
  clients: number | null
}

export interface WebsiteCampaignRow {
  value: string
  leads: number
  signups: number
  clients: number
}

export interface WebsiteSession {
  sessionId: string
  startedAt: string
  lastSeenAt: string
  entryPath: string | null
  currentPath: string | null
  country: string | null
  city: string | null
  referrer: string | null
  utmSource: string | null
  device: string | null
  /** PostHog GeoIP city centroid — what the live map places. */
  lat: number | null
  lng: number | null
  pageviews: number
  events: string[]
  identified: boolean
  /** Null for anyone but a platform admin — stripped by the API, not hidden here. */
  email: string | null
  personId: string | null
  personUrl: string | null
  replayUrl: string | null
  /** VRG-689 — pages, oldest first; `seconds` null = unknown. Null when PostHog could not be read. */
  pages: WebsitePageDuration[] | null
  /** VRG-689 — null for non-admins. See RecordingStatus. */
  recording: RecordingStatus | null
  /**
   * VRG-712 — the tracked-link code this session's events carry (PostHog `vera_invite`, set by
   * vera.inc after `/invite/<code>` once the visitor accepts analytics). Null for non-admins.
   */
  inviteCode: string | null
  /** VRG-712 — that code's tracked link (exact code match), or null (no code / no such link). */
  trackedLink: WebsiteTrackedLinkRef | null
}

export interface WebsiteTrackedLinkRef {
  id: string
  code: string
  label: string
  archived: boolean
}

export interface WebsitePageDuration {
  path: string | null
  at: string
  seconds: number | null
}

/**
 * VRG-689 — mirrors the API's RecordingStatus:
 *   recorded · none (didn't allow / declined) · expired (older than 3 months) ·
 *   processing (active minutes ago, not ingested yet) · check_failed (lookup errored)
 */
export type RecordingStatus = 'recorded' | 'none' | 'expired' | 'processing' | 'check_failed'

export interface WebsiteLiveResponse {
  computedAt: string
  windowMinutes: number
  posthog: SourceState
  sessions: WebsiteSession[]
}

/** VRG-712 — one page. `total` every match in the range; `capped` = PostHog's 50,000 ceiling hit. */
export interface WebsiteSessionsResponse {
  computedAt: string
  range: DayRange
  posthog: SourceState
  total: number
  capped: boolean
  offset: number
  limit: number
  sessions: WebsiteSession[]
}

export interface WebsiteSessionFilters {
  since: string
  until: string
  from?: string
  country?: string
  source?: string
  page?: string
  device?: string
  /** VRG-712 — server-side paging (rows per page, 1–100; rows to skip). */
  limit?: string
  offset?: string
}

/** VRG-712 — one visitor's sessions in the range, newest first (max 20; `capped` = more). */
export interface WebsiteJourneyResponse {
  computedAt: string
  range: DayRange
  posthog: SourceState
  personId: string | null
  capped: boolean
  sessions: WebsiteSession[]
}

export interface WebsiteJourneyQuery extends DayRange {
  personId?: string
  sessionId?: string
}

export interface WebsiteLeadRow {
  email: string
  signupId: string | null
  signupStatus: string | null
  companyId: string | null
  companyName: string | null
  firstSeenAt: string | null
  firstReferrer: string | null
  firstUtmSource: string | null
  firstUtmCampaign: string | null
  landingPath: string | null
  country: string | null
  formOpenedAt: string | null
  formSubmittedAt: string | null
  roiStartedAt: string | null
  roiCalculatedAt: string | null
  signupAt: string | null
  grantedAt: string | null
  demoAt: string | null
  companyAt: string | null
  becameClientAt: string | null
  lastContactAt: string
  posthogPersonUrl: string | null
  becameClientTracked: boolean
  /** VRG-712 — the People key (normalised email) when People has this person, else null. */
  peopleKey: string | null
  /** VRG-712 — the Sales → People row (VRG-727; was Leads) for the same email, else null. */
  salesLeadId: string | null
}

export interface WebsiteLeadsResponse {
  computedAt: string
  retentionDays: number
  total: number
  leads: WebsiteLeadRow[]
}

// ── VRG-690: page analysis + site flow (mirrors apps/api website-analytics.types.ts) ──

export interface WebsitePageRow {
  /** Normalised: lower-case, no query string or trailing slash, invite codes as `:code`. */
  path: string
  /** All visitors (Vercel). Null when Vercel is unavailable or the path is outside its top 100. */
  visitors: number | null
  pageviews: number | null
  /** Get started forms submitted on this page (Vercel). */
  leads: number | null
  /** Visitors with analytics on (PostHog). Null when PostHog is unavailable. */
  samplePageviews: number | null
  sampleVisitors: number | null
  /** Mean seconds over closed views; null when none was (never 0). */
  avgSeconds: number | null
  timedViews: number
  entries: number | null
  exits: number | null
  /** exits ÷ samplePageviews. */
  exitRate: number | null
}

/** VRG-734 — mirrors PosthogContentStatRow (apps/api …/website-analytics/types.ts). */
export interface WebsiteContentRow {
  path: string
  views: number
  visitors: number
  timedViews: number
  medianSeconds: number | null
  scrolledViews: number
  reached: { p25: number; p50: number; p75: number; p90: number }
  /**
   * `getStartedSubmitted` is the SIGN-UP — the Get started form going through, which is the
   * waitlist. It attributes to the piece because the form is a modal that pushes no URL, so the
   * submit carries the article's own `$pathname` (see the API's PosthogContentStatRow).
   */
  outcomes: { subscribed: number; getStartedOpened: number; getStartedSubmitted: number; contactSubmitted: number }
}

/** VRG-765 — /insights as a whole, one row per UTC day: the trend charts' series. */
export interface WebsiteContentDay {
  day: string
  views: number
  subscribed: number
  getStartedSubmitted: number
}

export interface WebsiteContentResponse {
  computedAt: string
  range: DayRange
  posthog: SourceState
  /** Null = PostHog failed or is not configured: missing, never zero. */
  rows: WebsiteContentRow[] | null
  /** Null = the daily read failed on its own; the table is unaffected. */
  days: WebsiteContentDay[] | null
}

export interface WebsitePagesResponse {
  computedAt: string
  range: DayRange
  sources: { vercel: SourceState; posthog: SourceState }
  vercelOrigin: 'live' | 'snapshot' | 'none'
  rows: WebsitePageRow[]
}

export type WebsiteFlowNodeKind = 'page' | 'other' | 'exit' | 'more'

export interface WebsiteFlowNode {
  id: string
  column: number
  kind: WebsiteFlowNodeKind
  path: string | null
  sessions: number
}

export interface WebsiteFlowLink {
  source: string
  target: string
  sessions: number
}

export interface WebsiteTopFlow {
  steps: string[]
  end: 'exit' | 'more'
  sessions: number
}

export interface WebsiteFlowsResponse {
  computedAt: string
  range: DayRange
  posthog: SourceState
  sessions: number
  depth: number
  nodes: WebsiteFlowNode[]
  links: WebsiteFlowLink[]
  topFlows: WebsiteTopFlow[]
}

/** VRG-714 — the funnel Sankey's node ids (see apps/api website-funnel-sankey.ts). */
export type WebsiteFunnelNodeId =
  | 'visited'
  | 'eng_form'
  | 'eng_roi'
  | 'eng_both'
  | 'left_visit'
  | 'lead_form'
  | 'lead_roi'
  | 'lead_both'
  | 'left_engaged'
  | 'joined'
  | 'left_lead'

export interface WebsiteFunnelNode {
  id: WebsiteFunnelNodeId
  label: string
  /** 0 Visited, 1 Engaged, 2 Lead, 3 Joined waitlist; a Left node's column is where they stopped. */
  column: 0 | 1 | 2 | 3
  kind: 'start' | 'stage' | 'left' | 'joined'
  /** People at this node. */
  n: number
}

export interface WebsiteFunnelLink {
  source: WebsiteFunnelNodeId
  target: WebsiteFunnelNodeId
  /** People on this band. */
  value: number
}

/** VRG-714 — `GET /internal/website-analytics/funnel`. Counts only. */
export interface WebsiteFunnelResponse {
  computedAt: string
  range: DayRange
  posthog: SourceState
  vera: SourceState
  /** Null when PostHog is unavailable. Visitors = people; visitorDays = person-days (Vercel's unit). */
  population: { visitors: number; visitorDays: number } | null
  nodes: WebsiteFunnelNode[]
  links: WebsiteFunnelLink[]
  waitlist: { total: number; inPopulation: number; outsidePopulation: number } | null
  /** People placed at a stage by a later step; `people` is distinct. */
  inferred: { engaged: number; lead: number; people: number }
}


// ── VRG-762: Core Web Vitals ─────────────────────────────────────────────────────

export const VITAL_NAMES = ['lcp', 'inp', 'cls', 'fcp', 'ttfb'] as const
export type VitalName = (typeof VITAL_NAMES)[number]

/**
 * `p75` is the 75th percentile — the figure Google grades on, never a mean. `samples` is
 * how many real visits it came from and is part of the reading: at vera.inc's traffic
 * most routes collect single digits over a month. `reliable` is the API's own verdict
 * (samples >= `minSamples`); the UI greys an unreliable figure rather than inventing its
 * own floor.
 */
export interface WebsiteVitalReading {
  p75: number | null
  samples: number
  reliable: boolean
}

export interface WebsiteVitalsRow {
  /** For routes this is the Next.js route PATTERN (`/industries/[slug]`), not a concrete path. */
  group: string
  lcp: WebsiteVitalReading
  inp: WebsiteVitalReading
  cls: WebsiteVitalReading
  fcp: WebsiteVitalReading
  ttfb: WebsiteVitalReading
  /** Daily p75 for `trendVital`, oldest first. `value: null` = no visits that day, NOT zero. */
  trend: Array<{ date: string; value: number | null; samples: number }>
}

export interface WebsiteVitalsResponse {
  computedAt: string
  range: DayRange
  /** Vercel only — PostHog sees consented visitors only, so it is the wrong instrument here. */
  sources: { vercel: SourceState }
  /** The window the p75 figures cover, which is NOT the selected range (Vercel pools at 7 days). */
  pooledWindow: { since: string; until: string; days: number }
  trendVital: VitalName
  minSamples: number
  byRoute: WebsiteVitalsRow[]
  byDevice: WebsiteVitalsRow[]
}

/**
 * `createApiCall`'s errorHandler RESOLVES with the error response rather than throwing
 * (services/index.ts), so the status is checked here — otherwise a 403 body would be
 * rendered as an empty funnel, and "not allowed" would read as "nobody visited".
 */
async function get<T>(url: string, what: string): Promise<T> {
  const response = await createApiCall({ method: 'GET', url })
  if (response.status !== 200) {
    const error = new Error(
      response?.data?.message || `Failed to load ${what}`,
    ) as Error & { status?: number }
    error.status = response?.status ?? 0
    throw error
  }
  return response.data as T
}

const qs = (params: Record<string, string | undefined>): string => {
  const entries = Object.entries(params).filter(
    (e): e is [string, string] => typeof e[1] === 'string' && e[1].trim() !== '',
  )
  return entries.length ? `?${new URLSearchParams(entries).toString()}` : ''
}

export const websiteAnalyticsApi = {
  overview(range: DayRange): Promise<WebsiteAnalyticsOverview> {
    return get(`/internal/website-analytics/overview${qs({ ...range })}`, 'website analytics')
  },
  pages(range: DayRange): Promise<WebsitePagesResponse> {
    return get(`/internal/website-analytics/pages${qs({ ...range })}`, 'page analysis')
  },
  vitals(range: DayRange, trend?: VitalName): Promise<WebsiteVitalsResponse> {
    return get(
      `/internal/website-analytics/vitals${qs({ ...range, trend })}`,
      'Core Web Vitals',
    )
  },
  content(range: DayRange): Promise<WebsiteContentResponse> {
    return get(`/internal/website-analytics/content${qs({ since: range.since, until: range.until })}`, 'content analytics')
  },
  flows(range: DayRange): Promise<WebsiteFlowsResponse> {
    return get(`/internal/website-analytics/flows${qs({ ...range })}`, 'site flow')
  },
  funnel(range: DayRange): Promise<WebsiteFunnelResponse> {
    return get(`/internal/website-analytics/funnel${qs({ ...range })}`, 'website funnel')
  },
  live(): Promise<WebsiteLiveResponse> {
    return get('/internal/website-analytics/live', 'live visitors')
  },
  sessions(filters: WebsiteSessionFilters): Promise<WebsiteSessionsResponse> {
    return get(
      `/internal/website-analytics/sessions${qs({ ...filters })}`,
      'visitor history',
    )
  },
  journey(q: WebsiteJourneyQuery): Promise<WebsiteJourneyResponse> {
    return get(`/internal/website-analytics/journey${qs({ ...q })}`, 'visitor journey')
  },
  leads(): Promise<WebsiteLeadsResponse> {
    return get('/internal/website-analytics/leads', 'leads')
  },
}
